import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Minus, Plus, ShoppingCart, ChevronLeft, ChevronRight,
  Heart, Store, Loader2, MessageSquare,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ShareButton from "@/components/ShareButton";
import ProductImage from "@/components/storefront/ProductImage";
import { trackEvent } from "@/lib/analytics";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * ProductDetailPage — TBC redesign (Feb 2026).
 * Image gallery + modifier groups + paid add-ons + special instructions +
 * quantity selector + dynamic total. Blocks Add-to-Cart until required
 * modifier groups are satisfied.
 */
export default function ProductDetailPage() {
  const { productId } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [selectedMods, setSelectedMods] = useState({}); // group_id -> [option_id]
  const [selectedAddOns, setSelectedAddOns] = useState({}); // add_on_id -> true
  const [special, setSpecial] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/products/${productId}`);
      setProduct(r.data);
      setGalleryIdx(0);
      setSelectedMods({});
      setSelectedAddOns({});
      setSpecial("");
      if (r.data?.business_id) {
        trackEvent(r.data.business_id, "product_view", { product_id: r.data.product_id });
      }
    } catch (e) {
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const g = Array.isArray(product.gallery_images) ? product.gallery_images.filter(Boolean) : [];
    return product.image_url ? [product.image_url, ...g.filter((s) => s !== product.image_url)] : g;
  }, [product]);

  // Dynamic total — recomputed on every mod/add-on/qty change.
  const derived = useMemo(() => {
    if (!product) return { unit: 0, total: 0, modIds: [], addOnIds: [], canAdd: false, missing: [] };
    let unit = Number(product.price || 0);
    const modIds = [];
    const missing = [];
    (product.modifier_groups || []).forEach((g) => {
      const picked = selectedMods[g.id] || [];
      picked.forEach((oid) => {
        const opt = (g.options || []).find((o) => o.id === oid);
        if (opt) unit += Number(opt.price_delta || 0);
      });
      modIds.push(...picked);
      const minReq = Math.max(g.required ? 1 : 0, Number(g.min_select || 0));
      if (picked.length < minReq) missing.push(g.name);
    });
    const addOnIds = [];
    (product.add_ons || []).forEach((a) => {
      if (selectedAddOns[a.id]) {
        unit += Number(a.price || 0);
        addOnIds.push(a.id);
      }
    });
    unit = Math.round(unit * 100) / 100;
    return {
      unit,
      total: Math.round(unit * quantity * 100) / 100,
      modIds,
      addOnIds,
      canAdd: missing.length === 0,
      missing,
    };
  }, [product, selectedMods, selectedAddOns, quantity]);

  const toggleMod = (group, optionId) => {
    setSelectedMods((prev) => {
      const cur = prev[group.id] || [];
      const has = cur.includes(optionId);
      const max = Math.max(1, Number(group.max_select || 1));
      let next;
      if (has) {
        next = cur.filter((x) => x !== optionId);
      } else if (max === 1) {
        next = [optionId];
      } else if (cur.length >= max) {
        toast.info(`You can only pick ${max} from "${group.name}".`);
        return prev;
      } else {
        next = [...cur, optionId];
      }
      return { ...prev, [group.id]: next };
    });
  };

  const addToCart = async () => {
    if (!user) { login(); return; }
    if (!derived.canAdd) {
      toast.error(`Please complete: ${derived.missing.join(", ")}.`);
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/cart/add`,
        {
          product_id: productId,
          quantity,
          selected_modifiers: derived.modIds,
          selected_add_ons: derived.addOnIds,
          special_instructions: special.trim() || null,
        },
        { withCredentials: true },
      );
      toast.success("Added to cart", { duration: 2500 });
      window.dispatchEvent(new CustomEvent("cart:updated"));
      navigate("/cart");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add to cart");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="aspect-square bg-tbc-cream rounded-2xl" />
            <div className="space-y-4">
              <div className="h-8 bg-tbc-cream rounded w-3/4" />
              <div className="h-6 bg-tbc-cream rounded w-1/4" />
              <div className="h-24 bg-tbc-cream rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="font-heading text-3xl text-tbc-navy mb-4">Product not found</h1>
          <Link to="/directory">
            <Button className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full">
              Browse directory
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const activeSrc = gallery[galleryIdx] || product.image_url;
  const hasModifiers = (product.modifier_groups || []).length > 0;
  const hasAddOns = (product.add_ons || []).length > 0;

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-0" data-testid="product-detail-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        {product.business && (
          <Link
            to={`/business/${product.business.business_id}`}
            className="inline-flex items-center text-sm text-gray-600 hover:text-tbc-navy font-body"
            data-testid="back-to-business"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to {product.business.name}
          </Link>
        )}
      </div>

      <section className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
            {/* Gallery */}
            <div>
              <div className="relative rounded-2xl overflow-hidden bg-tbc-cream">
                <ProductImage
                  src={activeSrc}
                  alt={product.name}
                  aspect="aspect-[4/3] sm:aspect-square"
                />
                {gallery.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setGalleryIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                      aria-label="Previous image"
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-tbc-navy/80 text-white flex items-center justify-center hover:bg-tbc-navy"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setGalleryIdx((i) => (i + 1) % gallery.length)}
                      aria-label="Next image"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-tbc-navy/80 text-white flex items-center justify-center hover:bg-tbc-navy"
                      data-testid="gallery-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <span
                      className="absolute bottom-2 right-2 bg-tbc-navy/85 text-white text-xs rounded-full px-2 py-0.5 font-body"
                      data-testid="gallery-count"
                    >
                      {galleryIdx + 1} / {gallery.length}
                    </span>
                  </>
                )}
                <button
                  type="button"
                  aria-label="Add to favorites"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-sm hover:bg-white"
                >
                  <Heart className="w-4 h-4 text-tbc-navy" />
                </button>
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 hidden sm:grid grid-cols-5 gap-2">
                  {gallery.slice(0, 5).map((src, i) => (
                    <button
                      key={`thumb-${i}`}
                      type="button"
                      onClick={() => setGalleryIdx(i)}
                      className={
                        "aspect-square rounded-lg overflow-hidden border-2 " +
                        (i === galleryIdx ? "border-tbc-gold" : "border-transparent")
                      }
                      data-testid={`gallery-thumb-${i}`}
                    >
                      <ProductImage src={src} alt="" aspect="aspect-square" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Configuration */}
            <div className="space-y-5">
              <div>
                <h1
                  className="font-heading text-2xl sm:text-3xl lg:text-4xl text-tbc-navy leading-tight"
                  data-testid="product-name"
                >
                  {product.name}
                </h1>
                <p className="mt-2 font-heading text-2xl sm:text-3xl text-tbc-gold" data-testid="product-price">
                  ${derived.unit.toFixed(2)}
                </p>
                {product.description && (
                  <p className="mt-3 text-gray-600 font-body leading-relaxed" data-testid="product-description">
                    {product.description}
                  </p>
                )}
                {product.business && (
                  <Link
                    to={`/business/${product.business.business_id}`}
                    className="mt-3 inline-flex items-center gap-2 text-sm text-tbc-navy hover:text-tbc-gold"
                  >
                    <Store className="w-4 h-4" /> Sold by {product.business.name}
                  </Link>
                )}
              </div>

              {(product.modifier_groups || []).map((g) => (
                <ModifierGroupBlock
                  key={g.id}
                  group={g}
                  picked={selectedMods[g.id] || []}
                  onToggle={(oid) => toggleMod(g, oid)}
                />
              ))}

              {hasAddOns && (
                <div data-testid="addons-block">
                  <h3 className="font-heading text-base text-tbc-navy">
                    Add extras <span className="text-gray-400 text-xs font-body">(Optional)</span>
                  </h3>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(product.add_ons || []).map((a) => {
                      const on = !!selectedAddOns[a.id];
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => setSelectedAddOns((p) => ({ ...p, [a.id]: !p[a.id] }))}
                          className={
                            "flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-all " +
                            (on
                              ? "bg-tbc-navy border-tbc-navy text-white"
                              : "bg-white border-gray-200 text-tbc-navy hover:border-tbc-gold/60")
                          }
                          data-testid={`addon-${a.id}`}
                        >
                          <div className="min-w-0">
                            <p className="font-body text-sm truncate">{a.name}</p>
                            <p className={"text-xs " + (on ? "text-tbc-gold" : "text-gray-500")}>
                              +${Number(a.price || 0).toFixed(2)}
                            </p>
                          </div>
                          <div
                            className={
                              "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 " +
                              (on ? "bg-tbc-gold text-tbc-navy" : "bg-tbc-cream text-tbc-navy")
                            }
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-heading text-base text-tbc-navy flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-tbc-gold" />
                  Special instructions <span className="text-gray-400 text-xs font-body">(Optional)</span>
                </h3>
                <Textarea
                  value={special}
                  onChange={(e) => setSpecial(e.target.value.slice(0, 300))}
                  rows={2}
                  placeholder="Add any special requests (no onions, extra crispy, etc.)"
                  className="mt-2 resize-none rounded-xl border-gray-200"
                  data-testid="special-instructions"
                />
              </div>

              {/* Quantity + Add to Cart */}
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      aria-label="Decrease quantity"
                      className="w-8 h-8 rounded-full flex items-center justify-center text-tbc-navy hover:bg-tbc-cream disabled:opacity-40"
                      disabled={quantity <= 1}
                      data-testid="quantity-decrease"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-heading text-tbc-navy" data-testid="quantity-input">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => q + 1)}
                      aria-label="Increase quantity"
                      className="w-8 h-8 rounded-full flex items-center justify-center text-tbc-navy hover:bg-tbc-cream"
                      data-testid="quantity-increase"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <Button
                    onClick={addToCart}
                    disabled={busy || !derived.canAdd}
                    className="flex-1 bg-tbc-gold hover:bg-tbc-gold/90 disabled:bg-tbc-gold/60 text-tbc-navy rounded-full py-6 text-base font-semibold"
                    data-testid="add-to-cart-button"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ShoppingCart className="w-5 h-5 mr-2" />
                    )}
                    Add to Cart • ${derived.total.toFixed(2)}
                  </Button>
                  <ShareButton
                    title={product.name}
                    text={`Check out ${product.name} on The Blk Community`}
                    variant="icon"
                    testId="share-product-button"
                  />
                </div>
                {!derived.canAdd && hasModifiers && (
                  <p className="mt-2 text-xs text-amber-600 font-body" data-testid="add-to-cart-blocked">
                    Please choose from: {derived.missing.join(", ")}.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ModifierGroupBlock({ group, picked, onToggle }) {
  const min = Math.max(group.required ? 1 : 0, Number(group.min_select || 0));
  const max = Math.max(1, Number(group.max_select || 1));
  return (
    <div data-testid={`modifier-group-${group.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-heading text-base text-tbc-navy">
          {group.name}{" "}
          {group.required ? (
            <span className="text-xs text-tbc-gold font-body">(Required)</span>
          ) : (
            <span className="text-xs text-gray-400 font-body">(Optional)</span>
          )}
        </h3>
        <span className="text-xs text-gray-500 font-body whitespace-nowrap">
          {picked.length} of {max} selected
        </span>
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(group.options || []).map((o) => {
          const on = picked.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              className={
                "flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-all " +
                (on
                  ? "bg-tbc-navy border-tbc-navy text-white"
                  : "bg-white border-gray-200 text-tbc-navy hover:border-tbc-gold/60")
              }
              data-testid={`modifier-option-${o.id}`}
            >
              <div className="min-w-0">
                <p className="font-body text-sm truncate">{o.name}</p>
                {Number(o.price_delta || 0) !== 0 && (
                  <p className={"text-xs " + (on ? "text-tbc-gold" : "text-gray-500")}>
                    {Number(o.price_delta) > 0 ? "+" : ""}${Number(o.price_delta).toFixed(2)}
                  </p>
                )}
              </div>
              <span
                className={
                  "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 " +
                  (on ? "bg-tbc-gold border-tbc-gold" : "border-gray-300")
                }
                aria-hidden="true"
              >
                {on && <span className="w-2 h-2 rounded-full bg-tbc-navy" />}
              </span>
            </button>
          );
        })}
      </div>
      {min > 0 && picked.length < min && (
        <p className="mt-1.5 text-xs text-amber-600 font-body">
          Choose at least {min}.
        </p>
      )}
    </div>
  );
}
